'use client';

import { useState } from 'react';
import {
    Modal,
    Heading,
    TextPassage,
    ButtonGroup,
    Button,
} from '@petsmart-ui/sparky';
import {
    typedFetch,
    getNextApiRoute,
    useAlertBanner,
    type AccountRemovePaymentResponse,
    useUserSession,
    BANNERS,
} from '@libs/common-next';

import { PROFILE_DATA } from '../../constants';
import type { RemoveCardModalProps } from './types';

import './RemoveCardModal.scss';

export function RemoveCardModal({
    modalOpen,
    modalClose,
    currentPayment,
}: RemoveCardModalProps) {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { setAlert } = useAlertBanner();
    const { updateProfileData, userData } = useUserSession();

    const hasActiveSubscription = userData?.payments?.some(
        payment => payment.hasSubscription
    );
    const isCardRemoveBlocked =
        currentPayment.isPrimary && hasActiveSubscription;

    const handleRemoveCard = async () => {
        setIsLoading(true);

        const response = await typedFetch<AccountRemovePaymentResponse>(
            getNextApiRoute(`account/payments/${currentPayment.cardId}`),
            {
                method: 'DELETE',
                additionalSuccessCodes: [404],
            }
        );

        if (response?.type && response.title && response?.status === 404) {
            setAlert({
                header: response?.title,
                text: response?.type,
                variant: 'error',
            });
        } else {
            await updateProfileData?.(PROFILE_DATA.ADDRESSES);
            await updateProfileData?.(PROFILE_DATA.PAYMENTS);

            setAlert({ ...BANNERS.success });
        }

        setIsLoading(false);
        modalClose(true);
    };

    return (
        <Modal
            dismissible
            isActive={modalOpen}
            onClose={() => modalClose()}
            className="remove-card-modal"
            data-testid="remove-card-modal"
        >
            <Modal.Header>
                <Heading tagName="h6" data-testid="remove-card-modal-heading">
                    Remove card
                </Heading>
            </Modal.Header>
            <Modal.Body>
                {isCardRemoveBlocked && (
                    <Heading
                        size="body-lg-bold"
                        tagName="h6"
                        data-testid="remove-card-modal-subheading"
                    >
                        This card cannot be removed
                    </Heading>
                )}
                <TextPassage
                    className={isCardRemoveBlocked ? 'card-remove-blocked' : ''}
                    size={isCardRemoveBlocked ? undefined : 'lg'}
                    data-testid="remove-card-modal-text"
                >
                    {isCardRemoveBlocked
                        ? 'Since you have an active subscription, a default card is required on your profile. Set another card as the default payment to remove this card.'
                        : 'Are you sure you want to remove this card from your account?'}
                </TextPassage>
            </Modal.Body>
            <Modal.Footer>
                <ButtonGroup align="right">
                    {isCardRemoveBlocked ? (
                        <Button
                            variant="primary"
                            text="Got it"
                            onClick={() => modalClose()}
                            size="lg"
                            data-testid="got-it-btn"
                        />
                    ) : (
                        <>
                            <Button
                                text="Keep card"
                                onClick={() => modalClose()}
                                size="lg"
                                data-testid="keep-card-btn"
                            />
                            <Button
                                className="danger-btn"
                                variant="primary"
                                text="Remove card"
                                onClick={handleRemoveCard}
                                loading={isLoading}
                                size="lg"
                                data-testid="remove-card-btn"
                            />
                        </>
                    )}
                </ButtonGroup>
            </Modal.Footer>
        </Modal>
    );
}
